import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, SearchIcon } from "lucide-react";
type Option = {
  label: string;
  value: string;
};

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  containerClassName?: string;
  disabled?: boolean;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options...",
  containerClassName = "",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleOptionChange = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const removeItem = (value: string) => {
    onChange(selectedValues.filter((v) => v !== value));
  };

  const getLabel = (value: string) => {
    return options.find((opt) => opt.value === value)?.label || value;
  };

  const filteredOptions = options.filter((opt) =>
    opt?.label?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isAllSelected = filteredOptions.length > 0 && filteredOptions.every(opt => selectedValues.includes(opt.value));
  const isSomeSelected = filteredOptions.length > 0 && !isAllSelected && filteredOptions.some(opt => selectedValues.includes(opt.value));
  
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isSomeSelected;
    }
  }, [isSomeSelected]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onChange(filteredOptions.map((opt) => opt.value));
    } else {
      onChange([]);
    }
  };

  const bgColorClass = containerClassName?.includes('bg-[') 
    ? containerClassName.match(/bg-\[#[0-9a-fA-F]+\]/)?.[0] 
    : null;

  return (
    <div className={`relative w-full text-sm ${containerClassName}`} ref={wrapperRef}>
      <div
        className={`border border-gray-300 rounded-lg p-2 flex flex-wrap gap-2 min-h-[40px] items-center justify-between ${
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
        } ${bgColorClass || "bg-white"}`}
        onClick={toggleDropdown}
        style={{ maxHeight: '80px', overflowY: 'auto' }}
      >
        <div className="flex gap-2 flex-wrap max-w-full" style={{ flex: 1 }}>
          {selectedValues.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            selectedValues.map((val) => (
              <span
                key={val}
                className="bg-blue-100 text-gray-600 px-2 py-1 flex items-center gap-1 whitespace-nowrap text-xs"
              >
                {getLabel(val)}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeItem(val);
                    }}
                    className="hover:text-gray-900 text-xs"
                  >
                    ✕
                  </button>
                )}
              </span>
            ))
          )}
        </div>

        <div
          className={`transform transition-transform duration-200 ml-2 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        >
          <ChevronDown/>
        </div>
      </div>

      {isOpen && !disabled && (
        <div 
          className="absolute bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] max-h-56 overflow-auto"
          style={{
            width: wrapperRef.current ? wrapperRef.current.offsetWidth : 'auto',
            top: '100%',
            left: 0,
            marginTop: '4px'
          }}
        >
          <div className="p-2 border-b relative">
            <input
              type="text"
              className="w-full pr-2 pl-8 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <SearchIcon className="absolute top-4 left-4 w-4 h-4 text-gray-500"/>
          </div>

          {filteredOptions.length === 0 ? (
            <div className="px-4 py-2 text-gray-400">No options found</div>
          ) : (
            <>
              <label className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer border-b">
                <input
                  type="checkbox"
                  className="mr-2"
                  ref={selectAllRef}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
                Select All ({filteredOptions.length})
              </label>
              {filteredOptions.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={selectedValues.includes(opt.value)}
                    onChange={() => handleOptionChange(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
